import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  useBusinessNoticeSelectList,
  useSendBusinessNotice,
} from "~/services/business/notify";
import { getToday } from "%/date";
import useOpenModal from "^/common/useOpenModal";
import { type ActivityOption, type RecipientOption } from "~/types/common";
import { expandRecipientValues } from "@/business/send/groupedRecipientOptions";
import dayjs from "dayjs";

export interface GalleryImage {
  ImgID: number;
  ImgURL: string;
}

export interface FormData {
  recipient: string | string[]; // 다중선택 지원
  animal: string;
  content: string;
  hour: string;
  minute: string;
  program: string;
  activity: string;
  bowel: string;
  meal: string;
  snack: string;
  water: string;
  [key: string]: string | string[] | ActivityOption | null; // 동적 필드를 위한 인덱스 시그니처 (다중선택 지원)
}

const useNotifySendSchedule = () => {
  const { data: selectList } = useBusinessNoticeSelectList();
  const sendNoticeMutation = useSendBusinessNotice();
  const [selectListData, setSelectListData] = useState<{
    recipients: RecipientOption[];
    activities: Array<{
      cdActivity: string;
      value: string;
      activityValues: ActivityOption[];
    }>;
  }>({
    recipients: [],
    activities: [],
  });

  // Form 관리
  const formMethods = useForm<FormData>({
    defaultValues: {},
    mode: "onChange",
  });

  const { setValue } = formMethods;

  // 날짜 상태 관리
  const [date] = useState(getToday());
  const [sendDate, setSendDate] = useState<Date | null>(null); // 발송 시간용 단일 날짜
  const [scheduleDates, setScheduleDates] = useState<Date[]>([]); // 스케줄용 다중 날짜
  const [selectedImgDate, setSelectedImgDate] = useState<Date | null>(null);

  // 모달 상태 관리
  const calendarModal = useOpenModal();
  const imageModal = useOpenModal();
  const imgDateModal = useOpenModal();

  // 이미지 상태 관리
  const [selectedImages, setSelectedImages] = useState<GalleryImage[]>([]);
  const [modalSelectedImages, setModalSelectedImages] = useState<
    GalleryImage[]
  >([]);

  // 토글 상태
  const [isToggle, setIsToggle] = useState(false);
  const [isTimeToggle, setIsTimeToggle] = useState(false);

  // 현재 시간을 계산하는 함수 (분은 올림 처리)
  const getCurrentTimeWithRoundedMinutes = () => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();

    // 분을 올림 처리 (0-9분 → 10분, 10-19분 → 20분, ..., 50-59분 → 00분+1시간)
    let roundedHour = currentHour;
    let roundedMinute = Math.ceil(currentMinute / 10) * 10;

    // 60분이 되면 1시간 추가하고 분은 0으로
    if (roundedMinute >= 60) {
      roundedHour = (roundedHour + 1) % 24;
      roundedMinute = 0;
    }

    return {
      hour: roundedHour.toString().padStart(2, "0"),
      minute: roundedMinute.toString().padStart(2, "0"),
    };
  };

  // 토글 상태 변경 시 현재 시간 자동 설정
  const handleTimeToggleChange = (checked: boolean) => {
    setIsTimeToggle(checked);

    if (checked) {
      // 토글을 켰을 때 현재 시간 자동 설정
      const currentTime = getCurrentTimeWithRoundedMinutes();
      setValue("hour", currentTime.hour);
      setValue("minute", currentTime.minute);
      console.log(
        `발송시간 자동 설정: ${currentTime.hour}시 ${currentTime.minute}분`
      );
    }
  };

  // 다이얼로그 상태
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState({
    title: "",
    message: "",
  });

  // selectList 데이터 업데이트
  useEffect(() => {
    if (selectList) {
      const newSelectListData = {
        recipients: selectList.recipients ?? [],
        activities: selectList.activities ?? [],
      };
      setSelectListData(newSelectListData);

      // 동적 activity 필드들의 기본값 설정
      const dynamicDefaults: Record<string, ActivityOption | null> = {};
      newSelectListData.activities.forEach((activity) => {
        dynamicDefaults[activity.cdActivity] = null;
      });

      // 폼 기본값 업데이트 (기존 값 유지하면서 동적 필드 추가)
      const currentValues = formMethods.getValues();
      formMethods.reset({
        ...currentValues,
        ...dynamicDefaults,
      });
    }
  }, [selectList, formMethods]);

  // 폼 제출 로직
  const bindSubmit = (data: FormData, isSave: boolean = false) => {
    console.log("폼 데이터:", data);

    // isTimeToggle이 true인데 sendDateTime이 필수인지 확인
    if (isTimeToggle && (!data.hour || !data.minute)) {
      alert("예약 발송을 선택한 경우 발송 시간을 설정해주세요.");
      return;
    }

    // 날짜와 시간 조합 - ISO 8601 형태로 변환
    // 발송 시간용 날짜 사용 (sendDate가 없으면 오늘 날짜 사용)
    const selectedSendDate = sendDate || new Date();
    // const sendDateTime = selectedSendDate;

    // 시간과 분이 유효한지 확인
    const hour = parseInt(data.hour) || 0;
    const minute = parseInt(data.minute) || 0;

    // 유효한 시간 범위인지 확인
    if (hour < 0 || hour > 23 || minute < 0 || minute > 59) {
      alert(
        "유효하지 않은 시간입니다. 시간은 0-23, 분은 0-59 사이여야 합니다."
      );
      return;
    }

    let finalSendDateTime;
    try {
      // dayjs를 사용하여 시간대 문제 해결
      const dateTime = dayjs(selectedSendDate)
        .hour(hour)
        .minute(minute)
        .second(0)
        .millisecond(0);

      // 생성된 dayjs 객체가 유효한지 확인
      if (!dateTime.isValid()) {
        throw new Error("Invalid date");
      }

      // 로컬 시간을 그대로 사용 (UTC 변환하지 않음)
      const year = dateTime.year();
      const month = String(dateTime.month() + 1).padStart(2, "0");
      const day = String(dateTime.date()).padStart(2, "0");
      const hours = String(dateTime.hour()).padStart(2, "0");
      const minutes = String(dateTime.minute()).padStart(2, "0");
      const seconds = String(dateTime.second()).padStart(2, "0");

      finalSendDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error("날짜/시간 변환 오류:", error);
      alert("날짜/시간 설정에 오류가 발생했습니다. 다시 확인해주세요.");
      return;
    }

    // selectList에서 받아온 데이터 구조를 그대로 유지하면서 사용자가 입력/선택한 값들만 변경
    const updatedSelectList = {
      // 필수 필드들 보장
      bizUserSeq: selectList?.bizUserSeq || 0,
      companySeq: selectList?.companySeq || 0,
      noticeSeq: selectList?.noticeSeq || null,
      notiTitle: typeof data.notiTitle === "string" ? data.notiTitle : null,
      notiContents: typeof data.content === "string" ? data.content : null,
      dtReservation: isTimeToggle ? finalSendDateTime : null, // isTimeToggle이 false면 null
      cdSender: selectList?.cdSender || "SENDER_OWNER",
      cdRecipient: selectList?.cdRecipient || "RECIPIENT_PET_OWNER",
      // 백엔드 스펙:
      //  - 저장하기:        tempSaveYn="Y", reservationSendFlag=false
      //  - 즉시 발송:       tempSaveYn="N", reservationSendFlag=false
      //  - 예약 발송(토글ON): tempSaveYn="Y", reservationSendFlag=true
      reservationSendFlag: !isSave && isTimeToggle,
      activityFlag:
        selectListData.activities.some(
          (activity) => data[activity.cdActivity]
        ) || false,
      tempSaveYn: isSave || isTimeToggle ? "Y" : "N",

      // 스케줄 정보 - 선택된 날짜들로 설정 (모달 우선, 없으면 본문)
      schedules: scheduleDates.map((date) => {
        try {
          // dayjs를 사용하여 시간대 문제 해결
          const dayjsDate = dayjs(date).startOf("day");

          // dayjs 객체가 유효한지 확인
          if (!dayjsDate.isValid()) {
            throw new Error("Invalid date");
          }

          return {
            scheduleSeq: null,
            notiRecvSeq: null,
            noticeSeq: null,
            cdDayOfWeek: null,
            dtSchedule: `${dayjsDate.year()}-${String(dayjsDate.month() + 1).padStart(2, "0")}-${String(dayjsDate.date()).padStart(2, "0")}T${String(dayjsDate.hour()).padStart(2, "0")}:${String(dayjsDate.minute()).padStart(2, "0")}:${String(dayjsDate.second()).padStart(2, "0")}`,
          };
        } catch (error) {
          console.error("스케줄 날짜 변환 오류:", error);
          return {
            scheduleSeq: null,
            notiRecvSeq: null,
            noticeSeq: null,
            cdDayOfWeek: null,
            dtSchedule: (() => {
              const today = dayjs().startOf("day");
              return `${today.year()}-${String(today.month() + 1).padStart(2, "0")}-${String(today.date()).padStart(2, "0")}T00:00:00`;
            })(), // 기본값으로 오늘 자정 사용
          };
        }
      }),

      // 일정 공지 타입의 selected를 true로 변경
      noticeTypes:
        selectList?.noticeTypes?.map((noticeType) => ({
          ...noticeType,
          selected: noticeType.cdNotiType === "NOTI_SCHEDULE", // 일정 공지만 true로 설정
        })) || [],

      // 선택된 반려견의 selected를 true로 변경
      // 보호자 단위 묶음 옵션 값("12,34")도 개별 petSeq로 전개해서 매칭
      recipients: (() => {
        const selectedPetSeqs = expandRecipientValues(data.recipient);
        return selectListData.recipients.map((recipient) => ({
          ...recipient,
          selected: selectedPetSeqs.has(recipient.petSeq.toString()),
        }));
      })(),

      // 선택된 활동들의 selected를 true로 변경
      activities: selectListData.activities.map((activity) => {
        const userSelectedValue = data[activity.cdActivity];
        if (userSelectedValue && typeof userSelectedValue === "object") {
          return {
            ...activity,
            activitySeq: null, // activitySeq 추가
            activityValues: activity.activityValues.map((option) => ({
              ...option,
              selected:
                option.cdActivityValue ===
                (userSelectedValue as ActivityOption).cdActivityValue,
            })),
          };
        }
        return {
          ...activity,
          activitySeq: null, // activitySeq 추가
        };
      }),

      // 선택된 이미지들
      photos: selectedImages.map((img) => ({
        notiPhotoSeq: null,
        fileSeq: img.ImgID,
        fileUrl: img.ImgURL,
      })),
    };

    console.log("전송 요청 데이터:", updatedSelectList);

    // API 호출
    sendNoticeMutation.mutate(updatedSelectList, {
      onSuccess: (response) => {
        console.log(isSave ? "저장 성공:" : "발송 성공:", response);
        const message = isSave
          ? "알림이 성공적으로 저장되었습니다."
          : "알림이 성공적으로 발송되었습니다.";
        let title = isSave ? "알림 저장 완료" : "알림 발송 완료";
        if (response.statusCode === 200) {
          setSuccessMessage({ title: title, message: message });
          setShowSuccessDialog(true); // 성공 다이얼로그 표시
        } else {
          title = isSave ? "알림 저장 실패" : "알림 발송 실패";
          setSuccessMessage({ title: title, message: message });
          setShowSuccessDialog(true);
        }
        // 폼 초기화 (필요시)
        // formMethods.reset();
      },
      onError: (error) => {
        console.error(isSave ? "저장 실패:" : "발송 실패:", error);
        const errorMessage = isSave
          ? "일정 공지 저장에 실패했습니다. 다시 시도해주세요."
          : "일정 공지 발송에 실패했습니다. 다시 시도해주세요.";
        alert(errorMessage);
      },
    });
  };

  // 모달에서 이미지 선택/해제
  const toggleImageSelection = (img: GalleryImage) => {
    setModalSelectedImages((prev) =>
      prev.find((item) => item.ImgID === img.ImgID)
        ? prev.filter((item) => item.ImgID !== img.ImgID)
        : [...prev, img]
    );
  };

  // 선택된 이미지 미리보기에서 삭제
  const removeSelectedImage = (img: GalleryImage) => {
    setSelectedImages((prev) =>
      prev.filter((item) => item.ImgID !== img.ImgID)
    );
    setModalSelectedImages((prev) =>
      prev.filter((item) => item.ImgID !== img.ImgID)
    );
  };

  // 모달에서 선택한 이미지 확정
  const handleImgModal = () => {
    // 기존 선택된 이미지와 새로 선택한 이미지를 합침
    const merged = [...selectedImages, ...modalSelectedImages];
    // ImgID 기준으로 중복 제거
    const unique = merged.filter(
      (img, idx, arr) => arr.findIndex((i) => i.ImgID === img.ImgID) === idx
    );
    setSelectedImages(unique);
    imageModal.closeModal();
  };

  return {
    // 폼 관련
    formMethods,

    // 선택 목록 데이터
    selectListData,

    // 날짜 관련
    date,
    sendDate,
    setSendDate,
    scheduleDates,
    setScheduleDates,
    selectedImgDate,
    setSelectedImgDate,

    // 모달 관련
    calendarModal,
    imageModal,
    imgDateModal,

    // 이미지 관련
    selectedImages,
    modalSelectedImages,
    toggleImageSelection,
    removeSelectedImage,
    handleImgModal,

    // 토글 관련
    isToggle,
    setIsToggle,
    isTimeToggle,
    setIsTimeToggle,
    handleTimeToggleChange,

    // 다이얼로그 관련
    showSuccessDialog,
    setShowSuccessDialog,
    successMessage,

    // 제출 관련
    bindSubmit,
    isSubmitting: sendNoticeMutation.isPending,
    submitError: sendNoticeMutation.error,
  };
};

export default useNotifySendSchedule;
